import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

interface LoginFormProps {
  email: string;
  password: string;
  loading: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSuccess: () => void;
  canSubmit?: boolean;
  onPolicyMiss?: () => void;
}

export function LoginForm({
  email,
  password,
  loading,
  onEmailChange,
  onPasswordChange,
  onSuccess,
  canSubmit = true,
  onPolicyMiss
}: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  React.useEffect(() => {

  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      onPolicyMiss?.();
      return;
    }

    if (!email || !password) {
      toast.error('נא למלא את כל השדות');
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      if (data.user) {
        await new Promise(resolve => setTimeout(resolve, 300));
        toast.success('התחברת בהצלחה!');
        onSuccess();
      }
    } catch (error: any) {
      let errorMessage = 'שגיאה בהתחברות';
      if (error?.message?.includes('Invalid login credentials')) {
        errorMessage = 'שם משתמש או סיסמה שגויים';
      }
      toast.error(errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 sm:mt-8 space-y-4 sm:space-y-6">
      <div className="space-y-3 sm:space-y-5">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <label htmlFor="email" className="block text-xs sm:text-sm font-bold text-gray-900 mb-1.5 sm:mb-2">
            אימייל
          </label>
          <motion.input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            whileFocus={{ scale: 1.01 }}
            className="appearance-none block w-full px-4 sm:px-5 py-3 sm:py-4 text-right border-2 border-gray-200 bg-white rounded-xl sm:rounded-2xl placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 sm:focus:ring-4 focus:ring-blue-100 transition-all duration-300 text-sm sm:text-base shadow-sm hover:shadow-lg"
            placeholder="your@email.com"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <label htmlFor="password" className="block text-xs sm:text-sm font-bold text-gray-900 mb-1.5 sm:mb-2">
            סיסמה
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              className="appearance-none block w-full px-4 sm:px-5 py-3 sm:py-4 pl-12 sm:pl-14 text-right border-2 border-gray-200 bg-white rounded-xl sm:rounded-2xl placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 sm:focus:ring-4 focus:ring-blue-100 transition-all duration-300 text-sm sm:text-base shadow-sm hover:shadow-lg"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setShowPassword(!showPassword);
              }}
              className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 text-gray-500 hover:text-blue-600 active:text-blue-700 transition-colors rounded-lg hover:bg-blue-50 focus:outline-none z-10"
              aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
          </div>
        </motion.div>
      </div>

      <motion.button
        whileHover={{ scale: loading || !canSubmit ? 1 : 1.02 }}
        whileTap={{ scale: loading || !canSubmit ? 1 : 0.98 }}
        type="submit"
        disabled={loading || !canSubmit}
        className="w-full flex justify-center items-center gap-2 sm:gap-3 py-3 sm:py-4 px-4 border-0 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl text-base sm:text-lg font-bold text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 sm:focus:ring-4 focus:ring-blue-200 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
        title={!canSubmit ? 'יש לאשר מדיניות לפני התחברות' : undefined}
      >
        {loading ? (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-4 h-4 sm:w-5 sm:h-5 border-2 sm:border-3 border-white border-t-transparent rounded-full"
            />
            <span>מתחבר...</span>
          </>
        ) : (
          <span>התחברות</span>
        )}
      </motion.button>
    </form>
  );
}
